import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/dashboard';
import Login from '../pages/authentication/Login';
import Logout from '../components/authentication/login/Logout';
// import Register from '../pages/authentication/Register';
import ResetPassword from '../pages/authentication/ResetPassword';
import NewPassword from '../pages/authentication/NewPassword';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import LoadingScreen from '../components/LoadingScreen';

// hooks
import useAuth from '../hooks/useAuth';

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user, userModules, dashboardRules} =  useAuth();
  let dashboard_routes = [
    { path: '/dashboard', element: <HomePage /> },
    { path: 'policies', element: <Policies /> },
    { path: 'policies/:policyId', element: <PolicyDetails/>},
    { path: 'sports', element: <Sports /> },
    { path: 'sports/create', element: <SportForm/> },
    { path: 'sports/:sportId', element: <SportDetails/> },
    { path: 'sports/:sportId/edit', element: <SportForm/> },
    { path: 'insurers/:insurerName', element: <InsurerList /> },
  ];  

  if(user && userModules && dashboardRules) {
    if("Certificate" in userModules) {
      let certificate_routes:any = []
  
      if(dashboardRules["CERTIFICATE_ANY_LIST"]) {
        certificate_routes.push({ path: 'certificates', element: <Certificates /> })
      }
  
      if(dashboardRules["CERTIFICATE_ANY_VIEW"]) {
        certificate_routes.push({ path: 'certificates/:certificateId', element: <CertificateDetails/> })
      }
  
      if(dashboardRules["CERTIFICATE_ANY_CREATE"]) {
        certificate_routes.push({ path: 'certificates/create', element: <CertificateForm/>})
        certificate_routes.push({ path: 'certificates/:certificateId/insureds', element: <CertificateInsuredsForm/>})
      }
  
      if(dashboardRules["CERTIFICATE_ANY_EDIT"]) {
        certificate_routes.push({ path: 'certificates/:certificateId/edit', element: <CertificateForm/>})
        certificate_routes.push({ path: 'certificates/:certificateId/renewal', element: <CertificateForm/>})
      }
      
  
      dashboard_routes = [
        ...dashboard_routes,
        ...certificate_routes
      ]
    }
  
    if("Insured" in userModules) {
      let insured_routes:any = []
  
      if(dashboardRules["INSURED_ANY_LIST"]) {
        insured_routes.push({ path: 'insureds', element: <Insured /> })
      }
  
      if(dashboardRules["INSURED_ANY_VIEW"]) {
        insured_routes.push({ path: 'insureds/:insuredId', element: <InsuredDetails/>})
      }
  
      if(dashboardRules["INSURED_ANY_CREATE"]) {
        insured_routes.push({ path: 'insureds/create', element: <InsuredCreate/>})
      }
  
      if(dashboardRules["INSURED_ANY_EDIT"]) {
        insured_routes.push({ path: 'insureds/:insuredId/edit', element: <InsuredCreate/>})
      }
      
      dashboard_routes = [
        ...dashboard_routes,
        ...insured_routes
      ]
    }

    // if("Product" in userModules) {
      let product_routes:any = []
  
    //   if(dashboardRules["PRODUCT_ANY_LIST"]) {
        product_routes.push({ path: 'products', element: <Products /> })
    //   }
  
    //   if(dashboardRules["PRODUCT_ANY_VIEW"]) {
        product_routes.push({ path: 'products/:productId', element: <ProductDetails/>})
    //   }
  
    //   if(dashboardRules["PRODUCT_ANY_CREATE"]) {
        product_routes.push({ path: 'products/create', element: <ProductForm/>})
    //   }
  
    //   if(dashboardRules["PRODUCT_ANY_EDIT"]) {
        product_routes.push({ path: 'products/:productId/edit', element: <ProductForm/>})
    //   }
      
      dashboard_routes = [
        ...dashboard_routes,
        ...product_routes
      ]
    // }


  }
  
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        // {
        //   path: 'register',
        //   element: (
        //     <GuestGuard>
        //       <Register />
        //     </GuestGuard>
        //   )
        // },
        {
          path: 'logout',
          element: <Logout/>
        },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> }
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: dashboard_routes 
    },
    { path: '/', element: <Navigate to="/auth/login" replace /> }
  ])
}

// Authentication
const HomePage = Loadable(lazy(() => import('../pages/HomePage')));
const Policies = Loadable(lazy(() => import('../pages/dashboard/Policy/List')));
const PolicyDetails = Loadable(lazy(() => import('../pages/dashboard/Policy/PolicyDetails')));
const Certificates = Loadable(lazy(() => import('../pages/dashboard/Certificate/List')));
const CertificateForm = Loadable(lazy(() => import('../pages/dashboard/Certificate/Form')));
const CertificateDetails = Loadable(lazy(() => import('../pages/dashboard/Certificate/Detail')));
const CertificateInsuredsForm = Loadable(lazy(() => import('../pages/dashboard/CertificateInsured/Form')));
const Sports = Loadable(lazy(() => import('../pages/dashboard/Sport/List')));
const SportDetails = Loadable(lazy(() => import('../pages/dashboard/Sport/Detail')));
const SportForm = Loadable(lazy(() => import('../pages/dashboard/Sport/Form')));
const Products = Loadable(lazy(() => import('../pages/dashboard/Product/List')));
const ProductDetails = Loadable(lazy(() => import('../pages/dashboard/Product/Detail')));
const ProductForm = Loadable(lazy(() => import('../pages/dashboard/Product/Form')));
const Insured = Loadable(lazy(() => import('../pages/dashboard/Insured/List')));
const InsuredDetails = Loadable(lazy(() => import('../pages/dashboard/Insured/Detail')));
const InsuredCreate = Loadable(lazy(() => import('../pages/dashboard/Insured/Form')));
const InsurerList = Loadable(lazy(() => import('../pages/dashboard/Insurers/List')));